import React from "react";
import {
  Box,
  Dialog,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material/";
import { styled } from "@mui/material/styles";
import common from "../../util/lang/common";
import t__ from "../../util/lang/util";
import CloseButton from "../CloseButton";
import AcceptanceMarks from "../AcceptanceMarks";
import AtmBox from "../AtmBox";
import acceptanceLevels from "../../util/lang/acceptanceLevels";
import countryDrawer, {
  mobilePaymentsFaqsLinkClassName,
} from "../../util/lang/countryDrawer";
import countryView from "../../util/lang/countryView";
import "../../util/theme/flags.css";

const StyledMobilePaymentsSection = styled(Box)(({ theme }) => ({
  [`& .${mobilePaymentsFaqsLinkClassName}`]: {
    textDecoration: "none",
    color: theme.palette.secondary.main,
    "&:hover": {
      textDecoration: "underline",
    },
    "&:focus": {
      outline: "2px solid",
      outlineColor: theme.palette.focus,
      outlineOffset: 2,
    },
    "&:focus:not(:focus-visible)": {
      outline: "none",
    },
    "&:focus-visible": {
      outline: "2px solid",
      outlineColor: theme.palette.focus,
      outlineOffset: 2,
    },
  },
}));

const CountryDetailsModal = ({ open, onClose, countryData }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const countryName =
    countryData?.translated_name ||
    countryData?.common_name ||
    countryData?.name ||
    "Country Details";

  let acceptanceMessage =
    countryData?.acceptanceCopy ||
    (countryData?.credit_level
      ? acceptanceLevels[theme.lang].headlinesAlt[countryData.credit_level]
      : "");
  if (
    typeof acceptanceMessage === "string" &&
    acceptanceMessage.includes("{region}")
  ) {
    const regionLabel =
      (countryData?.region &&
        countryDrawer[theme.lang].regions?.[countryData.region]) ||
      countryData?.region ||
      acceptanceLevels[theme.lang].regionFallback ||
      "";
    acceptanceMessage = acceptanceMessage.replace("{region}", regionLabel);
  }

  return (
    <Dialog
      aria-labelledby="country-details-modal-title"
      open={open}
      fullScreen={fullScreen}
      onClose={onClose}
      id="country-details-modal"
      maxWidth={false}
      scroll="body"
      PaperProps={{
        elevation: 0,
        sx: {
          width: "auto",
          maxWidth: "1120px",
          maxHeight: "none",
          height: "auto",
          overflowY: "visible",
          boxShadow: "none",
          marginLeft: 20,
          marginRight: 20,

          [theme.breakpoints.down("md")]: {
            margin: 3,
            width: "calc(100% - 48px)",
            maxWidth: "calc(100% - 48px)",
          },
        },
      }}
      sx={{
        overflowY: "auto",
        zIndex: 2147483600,
        "& .MuiPaper-rounded": {
          borderRadius: 3,
        },
        "& .MuiDialog-container": {
          alignItems: "center",
          width: "100%",
          minHeight: "100%",
          paddingY: fullScreen ? 5 : 0,
        },
      }}
    >
      <Box
        position="relative"
        sx={{
          right: 2,
          top: 2,
          px: fullScreen ? 5 : 6,
          py: fullScreen ? 5 : 6,
          margin: fullScreen ? 0 : 2,
          [theme.breakpoints.up("lg")]: {
            px: 8,
            py: 10,
          },
          [theme.breakpoints.up("xl")]: {
            px: 20,
            py: 18,
          },
        }}
      >
        <CloseButton
          onClick={onClose}
          alt={t__(theme.lang, common, "closeModal")}
          topRight
        />

        {countryData && (
          <Grid container rowSpacing={6} columnSpacing={fullScreen ? 0 : 12}>
            <Grid xs={12} lg={7} item style={{ display: "flex" }}>
              <Box>
                <Box
                  sx={{
                    px: 6,
                    py: 3,
                    bgcolor: "#d3d5eb",
                    borderRadius: 15,
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    mb: 10,
                    [theme.breakpoints.down("md")]: {
                      mt: 6,
                      mb: 6,
                    },
                  }}
                >
                  {countryData?.iso_a2 && (
                    <Box
                      component="span"
                      className={`flag:${countryData.iso_a2}`}
                      sx={{
                        flexShrink: 0,
                        fontSize: fullScreen ? "1rem" : "1.25rem",
                        mr: 2,
                      }}
                      aria-hidden
                    />
                  )}
                  <Typography
                    variant="h3"
                    component="h2"
                    id="country-details-modal-title"
                  >
                    <b>{countryName}</b>
                  </Typography>
                </Box>
                <Typography variant="body1" color="textPrimary" mb={4}>
                  <b>
                    {
                      acceptanceLevels[theme.lang]?.tooltip?.[
                        countryData?.credit_level
                      ]
                    }
                  </b>
                </Typography>
                {acceptanceMessage && (
                  <Typography variant="body1" color="textPrimary" mb={4}>
                    {acceptanceMessage}{" "}
                    {countryData.credit_level !== "NO COVERAGE" &&
                      "Some locations may not display acceptance decals, so always try presenting your card when paying."}
                  </Typography>
                )}

                <AcceptanceMarks
                  marks={countryData.credit_mark}
                  marksStyles={{
                    marginTop: theme.spacing(3),
                    marginRight: theme.spacing(3),
                  }}
                  style={{ flexWrap: "wrap" }}
                  variant="small"
                />
                {countryName === "China" && (
                  <StyledMobilePaymentsSection
                    mt={10}
                    sx={{
                      borderTop: `1px solid ${theme.palette.borders.light}`,
                      paddingTop: theme.spacing(10),
                    }}
                  >
                    <Typography variant="body1" color="textPrimary" mb={4}>
                      <b>
                        {t__(theme.lang, countryDrawer, "extraChina.headline")}
                      </b>
                    </Typography>
                    <Typography variant="body1" color="textPrimary">
                      {t__(theme.lang, countryDrawer, "extraChina.copy")}
                    </Typography>
                    <AcceptanceMarks
                      marks={["Alipay", "Weixin"]}
                      marksStyles={{ marginTop: theme.spacing(6) }}
                      style={{ flexWrap: "wrap" }}
                      variant="lg"
                    />

                    <ul
                      style={{
                        marginTop: theme.spacing(6),
                        listStyle: "none",
                        paddingLeft: 0,
                        marginLeft: 0,
                      }}
                    >
                      {t__(theme.lang, countryDrawer, "extraChina.faqsLink")}
                    </ul>
                    <Typography
                      variant="h5"
                      color="textSecondary"
                      component="p"
                      sx={{ marginTop: theme.spacing(6) }}
                    >
                      *{" "}
                      {t__(theme.lang, countryDrawer, "extraChina.disclosure")}
                    </Typography>
                  </StyledMobilePaymentsSection>
                )}
                {["Singapore", "Malaysia", "Thailand", "Philippines"].includes(
                  countryName
                ) && (
                  <StyledMobilePaymentsSection
                    mt={10}
                    sx={{
                      borderTop: `1px solid ${theme.palette.borders.light}`,
                      paddingTop: theme.spacing(10),
                    }}
                  >
                    <Typography variant={"body1"} color="textPrimary" mb={4}>
                      <b>
                        {(
                          t__(
                            theme.lang,
                            countryDrawer,
                            "extraSoutheastAsia.headlineTemplate"
                          ) ??
                          t__(
                            theme.lang,
                            countryDrawer,
                            "extraThailand.headline"
                          )
                        ).replace(/\{country\}/g, countryName)}
                      </b>
                    </Typography>
                    <Typography variant="body1" color="textPrimary">
                      {t__(theme.lang, countryDrawer, "extraThailand.copy")}
                    </Typography>
                    <AcceptanceMarks
                      marks={["GrabPay"]}
                      marksStyles={{ marginTop: theme.spacing(6) }}
                      style={{ flexWrap: "wrap" }}
                      variant="lg"
                    />

                    <ul
                      style={{
                        marginTop: theme.spacing(6),
                        listStyle: "none",
                        paddingLeft: 0,
                        marginLeft: 0,
                      }}
                    >
                      {t__(theme.lang, countryDrawer, "extraThailand.faqsLink")}
                    </ul>
                  </StyledMobilePaymentsSection>
                )}
                {countryData?.code === "USA" && (
                  <Typography
                    variant="h5"
                    color="textPrimary"
                    component="p"
                    id="usa-acceptance-footnote"
                    sx={{ marginTop: theme.spacing(10) }}
                  >
                    * {t__(theme.lang, countryView, "nilson")}
                  </Typography>
                )}
              </Box>
            </Grid>
            <Grid xs={12} lg={5} item style={{ display: "flex" }}>
              <AtmBox countryData={countryData} fixedFontSize />
            </Grid>
          </Grid>
        )}
      </Box>
    </Dialog>
  );
};

export default CountryDetailsModal;
